import { useMemo } from 'react';

import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';

import { colorsPalettes } from '@similarweb/styles';

import ChartService from 'services/chart/chartService';
import type { ChartObject, ChartTrendSeries, ChartSeries, ChartDataFormat } from 'services/chart/types';
import { formatPercentage, formatWithSuffix } from 'services/numberFormat';

import { getLegendBulletHTML } from './LegendBullet';

const formatValue = (val: number, format?: ChartDataFormat) => {
  if (val === 0) {
    return '0';
  }
  if (format === 'percentage') {
    return formatPercentage(val, 0);
  }
  if (format === 'number' || format === 'raw' || format === 'currency') {
    return String(formatWithSuffix(val));
  }
  return format ? `$${val} ${format}` : String(val);
};

const makeYAxis = ({ format, isSecondary }: { format?: ChartDataFormat; isSecondary: boolean }) => ({
  title: { text: undefined },
  labels: {
    formatter: ({ value }: { value: number | string }) => {
      return typeof value === 'number' ? formatValue(value, format) : value;
    },
    enabled: true,
    style: {
      fontSize: '11px',
      fontFamily: 'Roboto',
      color: colorsPalettes.carbon[400],
    },
  },
  opposite: isSecondary,
  visible: true,
});

export const GraphChart = ({ chartObject, chartData }: { chartObject: ChartObject; chartData: ChartTrendSeries[] }) => {
  const { chart, xAxis, series } = chartObject;

  const isMixed = useMemo(() => ChartService.hasMixedFormat(chartObject), [chartObject]);

  const formats = useMemo(
    () => Array.from(new Set((series ?? []).map((s: ChartSeries) => s?.dataFormat ?? 'number'))),
    [series]
  );

  const chartOptions = useMemo(() => {
    const isBarLike = chart.type === 'bar' || chart.type === 'column';
    const isLineLike = chart.type === 'line';
    const chartType = isBarLike ? 'column' : chart.type;

    const primaryFormat = formats[0] ?? series?.[0]?.dataFormat;
    const secondaryFormat = formats[1] ?? series?.[1]?.dataFormat;
    const tickmarkPlacement = xAxis?.categories ? (isBarLike ? 'between' : 'on') : undefined;
    const xAxisMin = isLineLike && xAxis?.categories?.length > 1 ? 0.5 : undefined;

    const xAxisMax = isLineLike && xAxis?.categories?.length > 1 ? xAxis?.categories?.length - 1.5 : undefined;

    const yAxis =
      isMixed && isBarLike
        ? [
            makeYAxis({ format: primaryFormat, isSecondary: false }),
            makeYAxis({ format: secondaryFormat, isSecondary: true }),
          ]
        : makeYAxis({ format: primaryFormat, isSecondary: false });

    const plotOptions: Record<string, unknown> = {
      [chartType]: {
        marker: {
          enabled: false,
        },
        enableMouseTracking: false,
        states: {
          hover: {
            enabled: false,
          },
          inactive: {
            enabled: false,
          },
        },
        animation: false,
      },
      series: {
        enableMouseTracking: false,
        states: {
          hover: {
            enabled: false,
          },
          inactive: {
            enabled: false,
          },
        },
        animation: false,
      },
    };

    return {
      title: {
        text: undefined,
      },
      yAxis,
      xAxis: {
        categories: xAxis?.categories,
        lineColor: colorsPalettes.carbon[200],
        lineWidth: 1,
        visible: true,
        minPadding: 0,
        maxPadding: 0,
        tickLength: 5,
        tickWidth: 1,
        tickmarkPlacement,
        min: xAxisMin,
        max: xAxisMax,
        tickColor: colorsPalettes.carbon[200],
        labels: {
          enabled: true,
          overflow: 'allow',
          allowOverlap: true,
          step: 1,
          style: {
            fontSize: '11px',
            fontFamily: 'Roboto',
            color: colorsPalettes.carbon[300],
          },
        },
        title: {
          text: undefined,
        },
      },
      legend: {
        enabled: true,
        align: 'right',
        verticalAlign: 'top',
        layout: 'vertical',
        x: -5,
        y: 0,
        itemStyle: {
          fontSize: '9px',
        },
        itemWidth: 150,
        maxItemWidth: 150,
        symbolWidth: 0,
        symbolHeight: 0,
        symbolRadius: 0,
        symbolPadding: 0,
        useHTML: true,
        labelFormatter: function (this: { name: string; color: string }) {
          return getLegendBulletHTML(this.color, this.name);
        },
      },
      tooltip: {
        enabled: false,
      },
      chart: {
        type: chartType,
        width: 422,
        height: 194,
        spacingBottom: 12,
        spacingTop: 24,
        spacingLeft: 12,
        spacingRight: 12,
      },
      credits: {
        enabled: false,
      },
      plotOptions,
      series: chartData,
    };
  }, [chartObject, chart.type, xAxis?.categories, chartData]);

  return <HighchartsReact highcharts={Highcharts} options={chartOptions} />;
};
